import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  TOOL_PREP_DOWNLOAD_FORMATS,
  PASSPORT_DELIVERABLE_OPTIONS,
  buildDownloadFileName,
  getDownloadFormat,
  stripDownloadFileBaseName,
} from "../../../../config/tools/toolPrepDownloadFormats";
import { defaultDownloadQualityId } from "../../../../config/tools/downloadQualityTiers";
import "./ToolPrepDownloadOptionsModal.css";

const HintTooltip = ({ text }) => (
  <span className="tpd-options__tooltip">
    <svg
      className="tpd-options__tooltip-icon"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
    <span className="tpd-options__tooltip-text" role="tooltip">
      {text}
    </span>
  </span>
);

/**
 * Editor-style Download Options strip.
 * variant: "editing" | "ai" | "passport"
 */
const ToolPrepDownloadOptionsModal = ({
  open = false,
  variant = "editing",
  previewUrl = null,
  previewKind = "image",
  sourceFileName = "",
  qualityOptions = [],
  hasTransparency = false,
  highQualityAlert = "",
  busy = false,
  onClose = () => {},
  onConfirm = () => {},
  title = "Download",
  defaultFormatId = "png",
}) => {
  const titleId = useId();
  const formatListId = useId();
  const formatWrapRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [formatOpen, setFormatOpen] = useState(false);
  const showQuality = variant === "editing" && qualityOptions.length > 0;
  const showPassport = variant === "passport";
  const showFormat = variant !== "document";

  const [fileBase, setFileBase] = useState(() =>
    stripDownloadFileBaseName(sourceFileName, "image")
  );
  const [formatId, setFormatId] = useState(() => getDownloadFormat(defaultFormatId).id);
  const [qualityId, setQualityId] = useState(() =>
    defaultDownloadQualityId(qualityOptions)
  );
  const [deliverableId, setDeliverableId] = useState("digital");

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    setFileBase(stripDownloadFileBaseName(sourceFileName, "image"));
    setFormatId(getDownloadFormat(defaultFormatId).id);
    setQualityId(defaultDownloadQualityId(qualityOptions));
    setDeliverableId("digital");
    setFormatOpen(false);
  }, [open, sourceFileName, qualityOptions, defaultFormatId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) {
        if (formatOpen) setFormatOpen(false);
        else onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose, formatOpen]);

  useEffect(() => {
    if (!formatOpen) return undefined;
    const onDoc = (e) => {
      if (!formatWrapRef.current?.contains(e.target)) {
        setFormatOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [formatOpen]);

  const selectedQuality = useMemo(
    () => qualityOptions.find((q) => q.id === qualityId) || null,
    [qualityOptions, qualityId]
  );

  const selectedFormat = getDownloadFormat(formatId);

  const jpgWarning =
    formatId === "jpg" && hasTransparency
      ? "JPG can’t keep transparency — background will flatten to white."
      : "";

  const qualityHint =
    selectedQuality?.softWatermark
      ? "Free 1K includes a light watermark."
      : selectedQuality?.needsUpscale
        ? "We’ll upscale before download (uses credits)."
        : highQualityAlert || "";

  const credits = selectedQuality?.effectiveCredits || 0;
  const confirmLabel =
    showQuality && credits > 0
      ? `Download · ${credits} credit${credits === 1 ? "" : "s"}`
      : showPassport && deliverableId === "digital_print"
        ? "Download photo + PDF"
        : "Download";

  if (!mounted || !open) return null;

  const handleConfirm = () => {
    if (busy) return;
    onConfirm({
      fileName: buildDownloadFileName(fileBase, formatId),
      fileBase,
      formatId,
      qualityId: showQuality ? qualityId : null,
      deliverableId: showPassport ? deliverableId : null,
      credits,
      softWatermark: Boolean(selectedQuality?.softWatermark),
      needsUpscale: Boolean(selectedQuality?.needsUpscale),
    });
  };

  const showTinyPreview =
    Boolean(previewUrl) && previewKind !== "pdf" && previewKind !== "doc";

  return createPortal(
    <div
      className="tpd-options"
      role="presentation"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        className="tpd-options__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="tpd-options__header">
          <div className="tpd-options__header-left">
            {showTinyPreview ? (
              <img
                src={previewUrl}
                alt=""
                className="tpd-options__thumb"
                draggable={false}
              />
            ) : null}
            <h2 id={titleId} className="tpd-options__title">
              {title}
            </h2>
          </div>
          <button
            type="button"
            className="tpd-options__close"
            aria-label="Close"
            disabled={busy}
            onClick={onClose}
          >
            ✕
          </button>
        </header>

        <div className="tpd-options__content">
          <div className="tpd-options__group">
            <div className="tpd-options__label-row">
              <label className="tpd-options__label" htmlFor="tpd-file-name">
                File name
              </label>
              <HintTooltip text="Name used when the file is saved to your device. The format extension is added automatically." />
            </div>
            <input
              id="tpd-file-name"
              type="text"
              className="tpd-options__input"
              value={fileBase}
              disabled={busy}
              onChange={(e) => setFileBase(e.target.value)}
              placeholder="Enter file name"
              autoComplete="off"
            />
          </div>

          {showPassport ? (
            <div className="tpd-options__group">
              <div className="tpd-options__label-row">
                <span className="tpd-options__label">Deliverable</span>
                <HintTooltip text="Choose a single digital photo, or include a print-ready PDF sheet." />
              </div>
              <div className="tpd-options__radios" role="radiogroup">
                {PASSPORT_DELIVERABLE_OPTIONS.map((opt) => (
                  <label
                    key={opt.id}
                    className={`tpd-options__radio${
                      deliverableId === opt.id ? " is-active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="tpd-deliverable"
                      value={opt.id}
                      checked={deliverableId === opt.id}
                      disabled={busy}
                      onChange={() => setDeliverableId(opt.id)}
                    />
                    <span className="tpd-options__radio-dot" aria-hidden="true" />
                    <span className="tpd-options__radio-body">
                      <span className="tpd-options__radio-title">{opt.label}</span>
                      <span className="tpd-options__radio-hint">
                        {opt.description}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}

          {showFormat ? (
            <div className="tpd-options__group">
              <div className="tpd-options__label-row">
                <span className="tpd-options__label" id={`${formatListId}-label`}>
                  Format
                </span>
                <HintTooltip text="File type for your download. PNG and WebP keep transparency; JPG is smaller but flattens it." />
              </div>
              <div className="tpd-options__select-wrap" ref={formatWrapRef}>
                <button
                  type="button"
                  className={`tpd-options__select${formatOpen ? " is-open" : ""}`}
                  disabled={busy}
                  aria-haspopup="listbox"
                  aria-expanded={formatOpen}
                  aria-labelledby={`${formatListId}-label`}
                  onClick={() => setFormatOpen((v) => !v)}
                >
                  <span>{selectedFormat.label}</span>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {formatOpen ? (
                  <ul
                    className="tpd-options__listbox"
                    role="listbox"
                    aria-labelledby={`${formatListId}-label`}
                    id={formatListId}
                  >
                    {TOOL_PREP_DOWNLOAD_FORMATS.map((fmt) => (
                      <li key={fmt.id} role="presentation">
                        <button
                          type="button"
                          role="option"
                          aria-selected={formatId === fmt.id}
                          className={`tpd-options__list-option${
                            formatId === fmt.id ? " is-active" : ""
                          }`}
                          onClick={() => {
                            setFormatId(fmt.id);
                            setFormatOpen(false);
                          }}
                        >
                          <span className="tpd-options__list-name">
                            {fmt.label}
                          </span>
                          <span className="tpd-options__list-desc">
                            {fmt.description}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
              <p className="tpd-options__desc">{selectedFormat.description}</p>
              {jpgWarning ? (
                <p className="tpd-options__warn">{jpgWarning}</p>
              ) : null}
            </div>
          ) : null}

          {showQuality ? (
            <div className="tpd-options__group">
              <div className="tpd-options__label-row">
                <span className="tpd-options__label">Quality</span>
                <HintTooltip text="Output resolution. Free 1K includes a soft watermark; 2K and 4K are clean and may use credits. Higher tiers can run AI upscale." />
              </div>
              <div className="tpd-options__quality-grid" role="radiogroup">
                {qualityOptions.map((q) => (
                  <label
                    key={q.id}
                    className={`tpd-options__quality-card${
                      qualityId === q.id ? " is-active" : ""
                    }${q.disabled ? " is-disabled" : ""}`}
                  >
                    <input
                      type="radio"
                      name="tpd-quality"
                      value={q.id}
                      checked={qualityId === q.id}
                      disabled={busy || q.disabled}
                      onChange={() => {
                        if (!q.disabled) setQualityId(q.id);
                      }}
                    />
                    <span className="tpd-options__quality-card-label">
                      {q.label}
                    </span>
                    {q.isCurrent ? (
                      <span className="tpd-options__quality-card-badge">
                        Current
                      </span>
                    ) : null}
                    <span
                      className={`tpd-options__quality-card-price${
                        !q.disabled && q.effectiveCredits === 0
                          ? " is-free"
                          : q.effectiveCredits > 0
                            ? " is-credits"
                            : ""
                      }`}
                    >
                      {q.disabled
                        ? "—"
                        : q.effectiveCredits > 0
                          ? `${q.effectiveCredits} Credit${
                              q.effectiveCredits === 1 ? "" : "s"
                            }`
                          : "Free"}
                    </span>
                    {(q.disabledReason || q.hoverHint) && (
                      <span className="tpd-options__quality-card-tip" role="tooltip">
                        {q.disabledReason || q.hoverHint}
                      </span>
                    )}
                  </label>
                ))}
              </div>
              {qualityHint ? (
                <p className="tpd-options__desc">{qualityHint}</p>
              ) : null}
            </div>
          ) : null}
        </div>

        <footer className="tpd-options__footer">
          <button
            type="button"
            className="tpd-options__download"
            disabled={busy || !String(fileBase || "").trim()}
            onClick={handleConfirm}
          >
            {busy ? "Preparing…" : confirmLabel}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default ToolPrepDownloadOptionsModal;
